import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, useCallback } from "react";
import { useTrackAnuncioView } from "@/hooks/use-track-anuncio-view";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  MapPin,
  X,
  Sparkles,
  TrendingUp,
  Building2,
  Train,
  ShieldCheck,
  Calendar,
} from "lucide-react";
import { BackToPortal } from "@/components/portal/BackToPortal";

import { getAnuncioPublico } from "@/lib/anuncios.functions";
import { createSolicitudVipAnuncio, getMiSolicitudAnuncio } from "@/lib/solicitudes.functions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatPrice } from "@/lib/utils";
import { buildGalleryItems, GallerySlide, GalleryThumb, type GalleryItem } from "@/components/portal/GalleryMedia";

export const Route = createFileRoute("/local-vip/$id")({
  component: LocalPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <main className="container mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold mb-2">No se pudo cargar el anuncio</h1>
        <p className="text-muted-foreground mb-6">{error.message}</p>
        <Button onClick={() => { reset(); router.invalidate(); }}>Reintentar</Button>
      </main>
    );
  },
  notFoundComponent: () => (
    <main className="container mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-semibold mb-2">Anuncio no encontrado</h1>
      <Link to="/portal-vip" className="text-primary underline">Volver al portal VIP</Link>
    </main>
  ),
});

function fmtEur(n: number | null | undefined, suffix = "") {
  if (n == null) return null;
  return `${formatPrice(n)} €${suffix}`;
}

function humanize(v: string | null | undefined) {
  if (!v) return null;
  const s = String(v).replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function LocalPage() {
  const { id } = Route.useParams();
  const fnGet = useServerFn(getAnuncioPublico);
  const fnCrear = useServerFn(createSolicitudVipAnuncio);
  const fnMia = useServerFn(getMiSolicitudAnuncio);

  useTrackAnuncioView(id);

  const q = useQuery({
    queryKey: ["anuncio-publico", id],
    queryFn: () => fnGet({ data: { id } }),
  });

  const miSolicitudQ = useQuery({
    queryKey: ["mi-solicitud-vip", id],
    queryFn: () => fnMia({ data: { anuncio_id: id } }),
  });

  const asideRef = useRef<HTMLElement>(null);
  const [asideVisible, setAsideVisible] = useState(false);
  const [solicitudOpen, setSolicitudOpen] = useState(false);

  useEffect(() => {
    const el = asideRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setAsideVisible(entry.isIntersecting),
      { threshold: 0.01, rootMargin: "0px 0px -60px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [q.data]);

  if (q.isLoading) {
    return (
      <main className="container mx-auto max-w-7xl px-4 py-8 space-y-4">
        <div className="h-8 w-1/2 rounded bg-muted animate-pulse" />
        <div className="h-[520px] rounded-2xl bg-muted animate-pulse" />
        <div className="h-40 rounded-2xl bg-muted animate-pulse" />
      </main>
    );
  }
  if (q.error) throw q.error;
  if (!q.data) return null;

  const { anuncio, media, usos, operacion, tipo_local, ciudad, barrio, licencia } = q.data as any;
  const op = (operacion?.slug ?? "").toLowerCase();

  const galeria = buildGalleryItems(media);

  const titulo = `${usos[0]?.nombre ?? tipo_local?.nombre ?? "Local"}${operacion ? ` en ${operacion.nombre}` : ""}${barrio ? ` en ${barrio.nombre}, ${ciudad?.nombre ?? ""}` : ciudad ? ` en ${ciudad.nombre}` : ""}`;

  return (
    <main className="bg-background">
      {/* Top bar */}
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 pt-6">
        <BackToPortal
          to="/portal-vip"
          label="Volver al portal VIP"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors"
        />
      </div>

      {/* Hero gallery */}
      <section className="container mx-auto max-w-7xl px-4 sm:px-6 pt-4">
        <Gallery items={galeria} titulo={titulo} />
      </section>

      {/* Title + address */}
      <section className="container mx-auto max-w-7xl px-4 sm:px-6 pt-8">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400">
            <Sparkles className="h-3 w-3" /> Off-market VIP
          </span>
          {operacion && (
            <span className="inline-flex rounded-full border bg-muted/50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {operacion.nombre}
            </span>
          )}
        </div>
        <h1 className="font-serif text-3xl sm:text-5xl font-bold leading-[1.05] tracking-tight break-words">
          {titulo}
        </h1>
        {anuncio.direccion_completa && anuncio.direccion_visible !== false && (
          <p className="mt-3 text-muted-foreground flex items-center gap-1.5 text-sm">
            <MapPin className="h-4 w-4" /> {anuncio.direccion_completa}
          </p>
        )}
        {anuncio.referencia && (
          <p className="mt-1.5 text-xs text-muted-foreground/70 font-medium tracking-wide">
            Ref. {anuncio.referencia}
          </p>
        )}
      </section>

      {/* Premium price hero */}
      <section className="container mx-auto max-w-7xl px-4 sm:px-6 pt-8">
        <PriceHero anuncio={anuncio} op={op} licencia={licencia} />
      </section>

      {/* Main grid */}
      <section className="container mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
          <div className="space-y-8">
            {anuncio.descripcion && (
              <SectionBlock title="Descripción">
                <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/85">
                  {anuncio.descripcion}
                </p>
              </SectionBlock>
            )}

            <DatosEconomicosBlock anuncio={anuncio} op={op} />

            <CaracteristicasBlock anuncio={anuncio} usos={usos} tipo_local={tipo_local} operacion={operacion} />

            <CondicionesContractualesBlock anuncio={anuncio} op={op} />

            <ConectividadBlock anuncio={anuncio} />

            <SolvenciaBlock anuncio={anuncio} />

            {anuncio.distribucion && Array.isArray(anuncio.distribucion) && anuncio.distribucion.length > 0 && (
              <SectionBlock title="Distribución">
                <ul className="text-sm divide-y divide-border/60">
                  {anuncio.distribucion.map((d: any, i: number) => (
                    <li key={i} className="flex justify-between py-3">
                      <span className="font-medium">{d.titulo}</span>
                      <span className="text-muted-foreground tabular-nums">{d.superficie ? `${d.superficie} m²` : "—"}</span>
                    </li>
                  ))}
                </ul>
              </SectionBlock>
            )}
          </div>

          <aside ref={asideRef} className="space-y-5 lg:sticky lg:top-20 lg:self-start">
            <SolicitudForm
              anuncioId={anuncio.id}
              fnCrear={fnCrear}
              fnMia={fnMia}
              open={solicitudOpen}
              onOpenChange={setSolicitudOpen}
            />
          </aside>
        </div>
      </section>

      {/* Mobile sticky CTA */}
      {!asideVisible && !solicitudOpen && !miSolicitudQ.data?.exists && (
        <div className="fixed bottom-[10px] left-0 right-0 px-4 z-50 lg:hidden">
          <Button
            type="button"
            onClick={() => {
              setSolicitudOpen(true);
              asideRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="w-full h-12 bg-foreground text-background hover:bg-foreground/90 font-semibold tracking-wide shadow-lg"
          >
            Enviar solicitud
          </Button>
        </div>
      )}
    </main>
  );
}

/* ─────────── Section primitives ─────────── */

function SectionBlock({ title, icon: Icon, children }: { title: string; icon?: any; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border bg-card shadow-[0_1px_0_rgba(0,0,0,0.02),0_8px_24px_-12px_rgba(0,0,0,0.08)]">
      <header className="flex items-center gap-2 px-6 pt-6">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <h2 className="font-serif text-xl font-semibold tracking-tight">{title}</h2>
      </header>
      <div className="px-6 pb-6 pt-4">{children}</div>
    </section>
  );
}

function StatRow({ items }: { items: Array<{ label: string; value: string | null }> }) {
  const list = items.filter((i) => i.value);
  if (list.length === 0) return null;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
      {list.map((it) => (
        <div key={it.label} className="flex items-start justify-between gap-4 border-b border-dashed border-border/60 pb-2.5">
          <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {it.label}
          </dt>
          <dd className="text-sm font-medium text-right tabular-nums">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ─────────── Price hero (L1 / L2 / L3) ─────────── */

type Level = { label: string; value: string };

function PriceHero({ anuncio, op, licencia }: { anuncio: any; op: string; licencia: any }) {
  let L1: Level | null = null;
  let L2: Level | null = null;
  const L3: Level[] = [];

  if (op === "alquiler") {
    L1 = anuncio.precio_alquiler != null
      ? { label: "Precio de alquiler", value: `${fmtEur(anuncio.precio_alquiler)} / mes` }
      : null;
  } else if (op === "rentabilidad") {
    if (anuncio.rent_bruta != null) {
      L1 = { label: "Rentabilidad bruta", value: `${anuncio.rent_bruta} %` };
    } else if (anuncio.rent_neta != null) {
      L1 = { label: "Rentabilidad neta", value: `${anuncio.rent_neta} %` };
    }
    L2 = anuncio.precio_venta != null ? { label: "Precio de venta", value: fmtEur(anuncio.precio_venta)! } : null;
    if (!L1 && L2) {
      L1 = L2;
      L2 = null;
    }
  } else if (op === "traspaso") {
    L1 = anuncio.precio_traspaso != null
      ? { label: "Precio de traspaso", value: fmtEur(anuncio.precio_traspaso)! }
      : null;
    L2 = anuncio.precio_alquiler != null
      ? { label: "Alquiler", value: `${fmtEur(anuncio.precio_alquiler)} / mes` }
      : null;
  } else if (op === "venta") {
    if (anuncio.rent_bruta != null) {
      L1 = { label: "Rentabilidad bruta", value: `${anuncio.rent_bruta} %` };
      L2 = anuncio.precio_venta != null ? { label: "Precio de venta", value: fmtEur(anuncio.precio_venta)! } : null;
    } else {
      L1 = anuncio.precio_venta != null
        ? { label: "Precio de venta", value: fmtEur(anuncio.precio_venta)! }
        : null;
    }
  } else {
    if (anuncio.precio_venta != null) L1 = { label: "Precio de venta", value: fmtEur(anuncio.precio_venta)! };
    else if (anuncio.precio_traspaso != null) L1 = { label: "Precio", value: fmtEur(anuncio.precio_traspaso)! };
    else if (anuncio.precio_alquiler != null) L1 = { label: "Alquiler", value: `${fmtEur(anuncio.precio_alquiler)} / mes` };
  }

  if (!L1) L1 = { label: "Precio", value: "Consultar" };

  return (
    <div className="relative overflow-hidden rounded-3xl border border-foreground/10 bg-gradient-to-br from-foreground via-foreground to-foreground/95 text-background shadow-[0_24px_60px_-20px_rgba(0,0,0,0.45)]">
      {/* gold accent */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-amber-400/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-20 h-72 w-72 rounded-full bg-amber-300/10 blur-3xl" />

      <div className={`relative grid gap-8 p-8 sm:p-10 ${L3.length > 0 ? "lg:grid-cols-[1.2fr_1fr]" : ""}`}>
        {/* L1 + L2 */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-10 sm:gap-16">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-300/90 mb-2 flex items-center gap-2">
              <span className="inline-block h-px w-6 bg-amber-300/60" />
              {L1.label}
            </div>
            <div className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight tabular-nums whitespace-nowrap">
              {L1.value}
            </div>
          </div>

          {L2 && (
            <div className="min-w-0 sm:pt-0 pt-4 border-t sm:border-t-0 border-background/15">
              <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-300/90 mb-2 flex items-center gap-2">
                <span className="inline-block h-px w-6 bg-amber-300/60" />
                {L2.label}
              </div>
              <div className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight tabular-nums whitespace-nowrap">
                {L2.value}
              </div>
            </div>
          )}
        </div>

        {/* L3 grid */}
        {L3.length > 0 && (
          <div className="grid grid-cols-2 gap-4 self-end">
            {L3.map((it) => (
              <div
                key={it.label}
                className="rounded-xl border border-background/15 bg-background/[0.04] backdrop-blur-sm px-4 py-3"
              >
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-background/55 mb-1 truncate">
                  {it.label}
                </div>
                <div className="text-sm font-semibold text-background leading-tight">
                  {it.value}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function usoActual(anuncio: any): string | null {
  // Best-effort: use estado_activos or tipo_operador as proxy for "uso actual"
  return humanize(anuncio.estado_activos) ?? anuncio.tipo_operador ?? null;
}

/* ─────────── Datos económicos ─────────── */

function DatosEconomicosBlock({ anuncio, op }: { anuncio: any; op: string }) {
  const items: Array<{ label: string; value: string | null }> = [];

  if (op === "rentabilidad") {
    if (anuncio.rent_bruta != null) items.push({ label: "Rentabilidad bruta", value: `${anuncio.rent_bruta} %` });
    if (anuncio.rent_neta != null) items.push({ label: "Rentabilidad neta", value: `${anuncio.rent_neta} %` });
    if (anuncio.precio_venta != null) items.push({ label: "Precio de venta", value: fmtEur(anuncio.precio_venta) });
  } else if (op === "traspaso") {
    if (anuncio.precio_traspaso != null) items.push({ label: "Precio traspaso", value: fmtEur(anuncio.precio_traspaso) });
    if (anuncio.precio_alquiler != null) items.push({ label: "Alquiler mensual", value: `${fmtEur(anuncio.precio_alquiler)} / mes` });
  } else if (op === "alquiler") {
    if (anuncio.precio_alquiler != null) items.push({ label: "Alquiler mensual", value: `${fmtEur(anuncio.precio_alquiler)} / mes` });
  } else if (op === "venta") {
    if (anuncio.precio_venta != null) items.push({ label: "Precio de venta", value: fmtEur(anuncio.precio_venta) });
    if (anuncio.rent_bruta != null) items.push({ label: "Rentabilidad bruta", value: `${anuncio.rent_bruta} %` });
    if (anuncio.rent_neta != null) items.push({ label: "Rentabilidad neta", value: `${anuncio.rent_neta} %` });
  } else {
    if (anuncio.precio_alquiler != null) items.push({ label: "Alquiler mensual", value: `${fmtEur(anuncio.precio_alquiler)} / mes` });
    if (anuncio.precio_traspaso != null) items.push({ label: "Precio traspaso", value: fmtEur(anuncio.precio_traspaso) });
    if (anuncio.precio_venta != null) items.push({ label: "Precio de venta", value: fmtEur(anuncio.precio_venta) });
    if (anuncio.rent_bruta != null) items.push({ label: "Rentabilidad bruta", value: `${anuncio.rent_bruta} %` });
    if (anuncio.rent_neta != null) items.push({ label: "Rentabilidad neta", value: `${anuncio.rent_neta} %` });
  }

  const honorarios = computeHonorariosBizaclia(anuncio, op);
  if (honorarios != null) items.push({ label: "Honorarios Bizaclia", value: fmtEur(honorarios) });

  if (honorarios != null) {
    if (op === "traspaso" && anuncio.precio_traspaso != null) {
      items.push({ label: "Precio final", value: fmtEur(Number(anuncio.precio_traspaso) + honorarios) });
    } else if ((op === "venta" || op === "rentabilidad") && anuncio.precio_venta != null) {
      items.push({ label: "Precio final", value: fmtEur(Number(anuncio.precio_venta) + honorarios) });
    }
  }

  if (items.length === 0) return null;

  return (
    <SectionBlock title="Datos económicos" icon={TrendingUp}>
      <StatRow items={items} />
    </SectionBlock>
  );
}

function computeHonorariosBizaclia(anuncio: any, op: string): number | null {
  const tiered = (price: number, tiers: Array<[number, number]>): number => {
    for (const [limit, rate] of tiers) {
      if (price <= limit) return price * rate;
    }
    return price * tiers[tiers.length - 1][1];
  };

  if (op === "venta" || op === "rentabilidad") {
    const price = Number(anuncio.precio_venta);
    if (!price || !isFinite(price)) return null;
    return tiered(price, [
      [500_000, 0.01],
      [1_000_000, 0.0075],
      [3_000_000, 0.005],
      [10_000_000, 0.0035],
      [Infinity, 0.0025],
    ]);
  }
  if (op === "traspaso") {
    const price = Number(anuncio.precio_traspaso);
    if (!price || !isFinite(price)) return null;
    return tiered(price, [
      [300_000, 0.01],
      [1_000_000, 0.0075],
      [3_000_000, 0.005],
      [Infinity, 0.0025],
    ]);
  }
  if (op === "alquiler") {
    const price = Number(anuncio.precio_alquiler);
    if (!price || !isFinite(price)) return null;
    return price * 12 * 0.01;
  }
  return null;
}


/* ─────────── Condiciones contractuales ─────────── */

function CondicionesContractualesBlock({ anuncio, op }: { anuncio: any; op: string }) {
  const items: Array<{ label: string; value: string | null }> = [];

  if (anuncio.contrato_fecha_inicio) items.push({ label: "Inicio de contrato", value: formatDate(anuncio.contrato_fecha_inicio) });
  if (anuncio.contrato_fecha_fin) items.push({ label: "Fin de contrato", value: formatDate(anuncio.contrato_fecha_fin) });
  if (anuncio.contrato_fecha_fin) items.push({ label: "Duración restante", value: formatDurationRemaining(anuncio.contrato_fecha_fin) });
  if (anuncio.periodo_obligado_cumplimiento) items.push({ label: "Periodo obligado de cumplimiento", value: anuncio.periodo_obligado_cumplimiento });
  if (anuncio.antiguedad_negocio) items.push({ label: "Antigüedad del negocio", value: anuncio.antiguedad_negocio });

  const hasStatRow = items.length > 0;
  const hasMotivo = anuncio.motivo_traspaso && op === "traspaso";
  const hasGarantias = !!anuncio.garantias;

  if (!hasStatRow && !hasMotivo && !hasGarantias) return null;
  return (
    <SectionBlock title="Condiciones contractuales" icon={Calendar}>
      {hasStatRow && <StatRow items={items} />}
      {hasGarantias && (
        <div className={(hasStatRow || hasMotivo) ? "mt-6" : ""}>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">
            Garantías
          </h3>
          <p className="text-sm font-medium leading-relaxed">{anuncio.garantias}</p>
        </div>
      )}
      {hasMotivo && (
        <div className={(hasStatRow || hasGarantias) ? "mt-6 pb-6 border-b border-dashed border-border/60" : ""}>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">
            Motivo del traspaso
          </h3>
          <p className="text-sm font-medium leading-relaxed">{anuncio.motivo_traspaso}</p>
        </div>
      )}
    </SectionBlock>
  );
}

function formatDurationRemaining(iso: string): string | null {
  const end = new Date(iso);
  const now = new Date();
  if (isNaN(end.getTime())) return null;
  if (end < now) return "Contrato finalizado";

  let years = end.getFullYear() - now.getFullYear();
  let months = end.getMonth() - now.getMonth();

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const parts: string[] = [];
  if (years > 0) parts.push(`${years} año${years > 1 ? "s" : ""}`);
  if (months > 0) parts.push(`${months} mes${months > 1 ? "es" : ""}`);
  if (parts.length === 0) return "Finaliza este mes";
  return parts.join(" y ");
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

/* ─────────── Conectividad y accesos ─────────── */

function ConectividadBlock({ anuncio }: { anuncio: any }) {
  if (!anuncio.conectividad_accesos) return null;
  return (
    <SectionBlock title="Conectividad y accesos" icon={Train}>
      <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/85">
        {anuncio.conectividad_accesos}
      </p>
    </SectionBlock>
  );
}

/* ─────────── Solvencia / Grupo empresarial ─────────── */

function SolvenciaBlock({ anuncio }: { anuncio: any }) {
  if (!anuncio.solvencia_grupo) return null;
  return (
    <SectionBlock title="Solvencia y grupo empresarial" icon={ShieldCheck}>
      <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/85">
        {anuncio.solvencia_grupo}
      </p>
    </SectionBlock>
  );
}

/* ─────────── Gallery ─────────── */

function Gallery({ items, titulo }: { items: GalleryItem[]; titulo: string }) {
  const [idx, setIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const touchStartX = useRef<number | null>(null);
  useEffect(() => { setIdx(0); }, [items.length]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + items.length) % items.length);
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % items.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [fullscreen, items.length]);

  if (items.length === 0) {
    return (
      <div className="h-96 rounded-2xl bg-muted flex items-center justify-center text-muted-foreground">
        Sin imágenes
      </div>
    );
  }

  const current = items[idx] ?? items[0];

  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].screenX;
    touchStartX.current = null;
    if (Math.abs(diff) < 50) return;
    if (diff > 0) next();
    else prev();
  };

  const secondary = items.slice(0, 4).filter((_, i) => i !== idx).slice(0, 2);

  return (
    <div className="space-y-3">
      <div className={items.length === 1 ? undefined : "grid gap-3 sm:grid-cols-[2fr_1fr]"}>
        <div
          className="relative rounded-2xl overflow-hidden bg-muted h-[360px] sm:h-[560px] cursor-zoom-in select-none group"
          onClick={() => setFullscreen(true)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <GallerySlide
            item={current}
            alt={current.titulo ?? titulo}
            imgClassName={`absolute inset-0 h-full w-full transition-transform duration-700 group-hover:scale-[1.02] ${items.length === 1 ? "object-contain" : "object-cover"}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
          {items.length > 1 && (
            <>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Anterior"
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black rounded-full shadow-xl backdrop-blur-sm h-11 w-11"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Siguiente"
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black rounded-full shadow-xl backdrop-blur-sm h-11 w-11"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
              <div className="absolute bottom-4 right-4 rounded-full bg-black/70 text-white px-3.5 py-1.5 text-xs font-medium backdrop-blur-md">
                {idx + 1} / {items.length}
              </div>
            </>
          )}
        </div>

        {secondary.length > 0 && (
          <div className="hidden sm:grid grid-rows-2 gap-3">
            {secondary.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setFullscreen(true)}
                className="relative rounded-2xl overflow-hidden bg-muted cursor-zoom-in group"
              >
                <GalleryThumb item={m} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              </button>
            ))}
          </div>
        )}
      </div>

      {items.length > 1 && (
        <div className="flex gap-2.5 overflow-x-auto pb-1">
          {items.map((m, i) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setIdx(i)}
              className={`relative shrink-0 h-16 w-24 rounded-lg overflow-hidden border-2 transition-all ${i === idx ? "border-amber-500 ring-2 ring-amber-500/20" : "border-transparent opacity-50 hover:opacity-100"}`}
            >
              <GalleryThumb item={m} className="h-full w-full object-cover bg-muted" />
            </button>
          ))}
        </div>
      )}

      {fullscreen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center select-none"
          onClick={() => setFullscreen(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <button
            type="button"
            onClick={() => setFullscreen(false)}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-10"
            aria-label="Cerrar"
          >
            <X className="h-8 w-8" />
          </button>

          {items.length > 1 && (
            <>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Anterior"
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-sm z-10"
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Siguiente"
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-sm z-10"
              >
                <ChevronRight className="h-8 w-8" />
              </Button>
            </>
          )}

          <div className={current.kind === "image" ? undefined : "relative aspect-video w-[90vw] max-w-6xl max-h-[85vh]"} onClick={(e) => e.stopPropagation()}>
            <GallerySlide
              item={current}
              alt={current.titulo ?? titulo}
              imgClassName="max-h-[90vh] max-w-[90vw] object-contain"
            />
          </div>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 text-white px-4 py-1 text-sm backdrop-blur-sm">
            {idx + 1} / {items.length}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────── Características ─────────── */

function CaracteristicasBlock({ anuncio, usos, tipo_local, operacion }: { anuncio: any; usos: any[]; tipo_local: any; operacion: any }) {
  const features: Array<{ label: string; value: string | null }> = [];
  if (anuncio.t_superficie != null) features.push({ label: "Superficie", value: `${anuncio.t_superficie} m²` });
  if (tipo_local) features.push({ label: "Tipo de local", value: tipo_local.nombre });
  if (operacion) features.push({ label: "Operación", value: operacion.nombre });
  if (anuncio.tipo_zona) features.push({ label: "Tipo de zona", value: humanize(anuncio.tipo_zona) });
  if (anuncio.nivel_transito) features.push({ label: "Tránsito peatonal", value: humanize(anuncio.nivel_transito) });
  if (anuncio.estado_inmueble) features.push({ label: "Estado del activo", value: humanize(anuncio.estado_inmueble) });
  if (anuncio.t_num_aseos != null) features.push({ label: "Aseos", value: String(anuncio.t_num_aseos) });
  if (anuncio.h_aforo != null) features.push({ label: "Aforo", value: String(anuncio.h_aforo) });
  if (anuncio.t_ancho_fachada != null) features.push({ label: "Ancho de fachada", value: `${anuncio.t_ancho_fachada} m` });
  if (anuncio.t_parking_plazas != null) features.push({ label: "Plazas parking", value: String(anuncio.t_parking_plazas) });
  if (anuncio.e_consumo) features.push({ label: "Consumo energético", value: String(anuncio.e_consumo).toUpperCase().replace(/_/g, "") });
  if (anuncio.e_emisiones) features.push({ label: "Emisiones", value: String(anuncio.e_emisiones).toUpperCase().replace(/_/g, "") });

  const bools: Array<[string, boolean]> = [
    ["A pie de calle", !!anuncio.a_pie_de_calle],
    ["Esquinero", !!anuncio.t_esquinero],
    ["Mucha luz", !!anuncio.t_mucha_luz],
    ["Amueblado", !!anuncio.t_amueblado],
    ["Aire acondicionado", !!anuncio.t_aire_acondicionado],
    ["Cocina", !!anuncio.t_cocina],
    ["Agua", !!anuncio.t_agua],
    ["Alarma", !!anuncio.t_alarma],
    ["Salida emergencia", !!anuncio.t_salida_emergencia],
    ["Acceso movilidad reducida", !!anuncio.t_acceso_movilidad_reducida],
    ["Salida de humos", !!anuncio.h_salida_humos],
    ["Cámara frigorífica", !!anuncio.h_camara_frigorifica],
    ["Obrador", !!anuncio.h_obrador],
    ["Terraza", !!anuncio.h_terraza],
    ["Parking", !!anuncio.t_parking],
    ["Muelle de carga", !!anuncio.n_muelle_carga],
    ["Vado", !!anuncio.n_vado],
  ].filter(([, v]) => v) as Array<[string, boolean]>;

  if (features.length === 0 && bools.length === 0 && usos.length === 0) return null;

  return (
    <SectionBlock title="Características" icon={Building2}>
      <div className="space-y-5">
        {features.length > 0 && <StatRow items={features} />}
        {usos.length > 0 && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">
              Uso actual
            </div>
            <div className="flex flex-wrap gap-1.5">
              {usos.map((u) => (
                <Badge key={u.id} variant="secondary" className="rounded-full px-3 py-1 text-xs">{u.nombre}</Badge>
              ))}
            </div>
          </div>
        )}
        {bools.length > 0 && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">
              Equipamiento
            </div>
            <div className="flex flex-wrap gap-1.5">
              {bools.map(([label]) => (
                <span key={label} className="inline-flex items-center rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-foreground/80">
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </SectionBlock>
  );
}

/* ─────────── Formulario ─────────── */

function SolicitudForm({
  anuncioId,
  fnCrear,
  fnMia,
  open: openProp,
  onOpenChange,
}: {
  anuncioId: string;
  fnCrear: (args: { data: { anuncio_id: string; mensaje: string } }) => Promise<{ ok: true; created_at: string; already: boolean }>;
  fnMia: (args: { data: { anuncio_id: string } }) => Promise<{ exists: false } | { exists: true; created_at: string }>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp !== undefined ? openProp : internalOpen;
  const setOpen = useCallback((v: boolean) => {
    onOpenChange?.(v);
    if (openProp === undefined) setInternalOpen(v);
  }, [onOpenChange, openProp]);

  const [mensaje, setMensaje] = useState("Hola, estoy interesado/a en este local. ¿Podríamos hablar?");

  const queryClient = useQueryClient();

  const existQ = useQuery({
    queryKey: ["mi-solicitud-vip", anuncioId],
    queryFn: () => fnMia({ data: { anuncio_id: anuncioId } }),
  });

  const [sentAt, setSentAt] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => fnCrear({ data: { anuncio_id: anuncioId, mensaje: mensaje.trim() } }),
    onSuccess: (res) => {
      setSentAt(res.created_at);
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["mi-solicitud-vip", anuncioId] });
      (async () => {
        try {
          const [{ trackConversion }, { supabase }] = await Promise.all([
            import("@/lib/tracking"),
            import("@/integrations/supabase/client"),
          ]);
          const { data } = await supabase.auth.getSession();
          trackConversion("generate_lead", {
            userId: data.session?.user.id ?? null,
            listingId: anuncioId,
          });
        } catch {
          // silencioso
        }
      })();
      toast.success("Solicitud enviada. Te contactaremos pronto.");
    },
    onError: (e: any) => toast.error(e?.message ?? "No se pudo enviar la solicitud"),
  });

  const existingAt = sentAt ?? (existQ.data && existQ.data.exists ? existQ.data.created_at : null);

  const formatFecha = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
    } catch {
      return iso;
    }
  };

  return (
    <Card className="border-foreground/10 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.12)]">
      <CardContent className="p-6 space-y-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400 mb-1">
            Contacto privado
          </div>
          <h2 className="font-serif text-xl font-semibold">Solicitar información</h2>
          <p className="text-sm text-muted-foreground mt-1">Recibirás respuesta del anunciante en breve.</p>
        </div>

        {existingAt ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30 px-4 py-3 text-sm text-emerald-900 dark:text-emerald-200">
            Has enviado una solicitud el {formatFecha(existingAt)}.
          </div>
        ) : open ? (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (mensaje.trim().length === 0) return;
              mut.mutate();
            }}
          >
            <div>
              <Label htmlFor="mensaje">Mensaje</Label>
              <Textarea
                id="mensaje"
                rows={4}
                required
                autoFocus
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={mut.isPending}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={mut.isPending}
                className="flex-1 bg-foreground text-background hover:bg-foreground/90 font-semibold tracking-wide"
              >
                {mut.isPending ? "Enviando…" : "Enviar"}
              </Button>
            </div>
          </form>
        ) : (
          <Button
            type="button"
            onClick={() => setOpen(true)}
            disabled={existQ.isLoading}
            className="w-full bg-foreground text-background hover:bg-foreground/90 font-semibold tracking-wide"
          >
            Enviar solicitud
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
